/**
 * useAuth Hook - Main authentication logic
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { AUTH_MODES, INPUT_TYPES, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../types';
import * as authApi from '../services/authApi';
import { validateMobileOrEmail, validateOtp, validatePassword } from '../utils/validation';
import { storeUserSession, getRedirectPath, clearUserSession } from '../utils/storage';
import { encryptData } from '../../api/Services/encryptData';
import { Base64 } from "js-base64";
const REGISTER = { SUCCESS_REGISTRATION: 'REGISTER_SUCCESS' };

export function useAuth() {
  const navigate = useNavigate();
  const {
    identifier,
    identifierType,
    otp,
    password,
    confirmPassword,
    registrationData,
    patientCode,
    setIdentifier,
    setIdentifierType,
    setOtp,
    setPassword,
    setLoading,
    setError,
    setOtpSent,
    setIsNewUser,
    setPatientCode,
    setNeedsPassword,
    setMode,
    closeAuth,
  } = useAuthContext();
  
  /**
   * Send OTP to mobile or email
   */
  const sendOtp = useCallback(async () => {
    const validation = validateMobileOrEmail(identifier);
    
    if (!validation.isValid) {
      setError(validation.error);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let result;
      
      if (validation.type === INPUT_TYPES.MOBILE) {
        result = await authApi.sendMobileOtp(identifier);
      } else {
        result = await authApi.sendEmailOtp(identifier);
      }
      
      setIdentifierType(validation.type);
      setOtpSent(true);
      
      // Check if user is registered
      if (result?.data?.isUserRegistered === 'N') {
        setIsNewUser(true);
      }
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [identifier, setIdentifierType, setLoading, setError, setOtpSent, setIsNewUser]);
  
  /**
   * Verify OTP
   */
  const verifyOtp = useCallback(async () => {
    const otpValidation = validateOtp(otp);
    
    if (!otpValidation.isValid) {
      setError(otpValidation.error);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = identifierType === INPUT_TYPES.MOBILE
        ? { mobileNumber: identifier, otp: parseInt(otp) }
        : { email: identifier, otp: parseInt(otp) };
      
      const result = await authApi.verifyOtp(data);
      
      if (result?.data?.isUserRegistered === 'N') {
        // New user - needs registration
        setIsNewUser(true);
        setMode(AUTH_MODES.REGISTRATION_FORM);
        return true;
      }
      
      if (result?.data?.isUserRegistered === 'Y') {
        // Existing user - login successful
        storeUserSession(result.data);
        
        closeAuth();
        
        const redirectPath = getRedirectPath();
        navigate(redirectPath);
        
        return true;
      }
      
      return false;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [otp, identifier, identifierType, navigate, setLoading, setError, setIsNewUser, setMode, closeAuth]);
  
  /**
   * Login with password
   */
  const loginWithPassword = useCallback(async () => {
    if (!password) {
      setError(ERROR_MESSAGES.REQUIRED_PASSWORD);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const encryptedPayload = encryptData({
        email: identifier,
        password,
      });
      
      const result = await authApi.loginWithPassword(encryptedPayload);
      
      if (result?.success === false) {
        setError(result.message || ERROR_MESSAGES.GENERIC_ERROR);
        return false;
      }
      
      storeUserSession(result.data);
      
      closeAuth();
      
      const redirectPath = getRedirectPath();
      navigate(redirectPath);
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [password, identifier, navigate, setLoading, setError, closeAuth]);
  
  /**
   * Check if user has password and switch to password/OTP mode
   */
  const checkPasswordAndProceed = useCallback(async () => {
    const validation = validateMobileOrEmail(identifier);
    
    if (!validation.isValid) {
      setError(validation.error);
      return false;
    }
    
    // Only check password for email login
    if (validation.type === INPUT_TYPES.EMAIL) {
      setLoading(true);
      setError('');
      
      try {
        const result = await authApi.checkPasswordExists(identifier);
        
        if (result.hasPassword) {
          setNeedsPassword(false);
          // User has password, show password field
          return 'password';
        } else {
          // User needs to set password after OTP verification
          setNeedsPassword(true);
          return 'otp';
        }
      // REPLACE WITH (add 'no patient found' line):
      } catch (error) {
        const errorMsg = error.message?.toLowerCase() || '';
        if (errorMsg.includes('not found') || 
            errorMsg.includes('does not exist') ||
            errorMsg.includes('not registered') ||
            errorMsg.includes('user not present') ||
            errorMsg.includes('no patient found')) {  // ✅ ADD THIS LINE
          return 'not_found';
        }
        setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
        return false;
      }finally {
        setLoading(false);
      }
    }
    
    // For mobile, always use OTP
    return 'otp';
  }, [identifier, setLoading, setError, setNeedsPassword]);
  
  /**
   * Check if email user exists
   */
  const checkEmailExists = useCallback(async (email) => {
    setLoading(true);
    setError('');
    
    try {
      const result = await authApi.checkPasswordExists(email);
      // If we get here, user exists
      return { exists: true, hasPassword: result.hasPassword };
    } catch (error) {
      const errorMsg = error.message?.toLowerCase() || '';
      if (errorMsg.includes('not found') || 
          errorMsg.includes('does not exist') ||
          errorMsg.includes('not registered') ||
          errorMsg.includes('user not present')) {
        return { exists: false, hasPassword: false };
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);
  
  /**
   * Register new user
   */
  const register = useCallback(async (formData, consentInfo) => {
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        email: formData.email || '',
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        mobileNumber: formData.mobile,
        dob: formData.dob,
        salutation: formData.salutation,
        gender: formData.gender || '',
      };
      
      const result = await authApi.registerUser(payload);
      
      // Submit consent
      if (consentInfo?.tcVersion && consentInfo?.privacyVersion) {
        try {
          await authApi.submitConsent({
            createdBy: 'CUREBAYADMIN',
            privacyPolicyConsent: 1,
            sourceChannel: 'B2C',
            status: 1,
            termsAndConditionConsent: 1,
            type: 'P',
            patientId: result?.data?.patientCode,
            privacyVersionNumber: consentInfo.privacyVersion,
            tcVersionNumber: consentInfo.tcVersion,
          });
        } catch (consentError) {
          console.error('Consent submission failed:', consentError);
        }
      }
      
      // Store user session
      storeUserSession(result.data);
      
      closeAuth();
        const redirectPath = getRedirectPath();
        if(redirectPath){
          navigate(redirectPath);
        }else{
          navigate('/myprofile');
        }
      
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate, setLoading, setError, closeAuth]);
  
  /**
   * Set new password
   */
  const setNewPassword = useCallback(async () => {
    const passwordValidation = validatePassword(password);
    
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return false;
    }
    
    if (password !== confirmPassword) {
      setError(ERROR_MESSAGES.PASSWORDS_MISMATCH);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const encryptedPayload = encryptData({
        patientCode,
        password,
      });
      
      const result = await authApi.setPassword(encryptedPayload);
      
      if (result?.status === 200) {
        closeAuth();
        return true;
      }
      
      return false;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [password, confirmPassword, patientCode, setLoading, setError, closeAuth]);
  
  /**
   * Send forgot password OTP
   */
  const sendForgotPasswordOtp = useCallback(async () => {
    const validation = validateMobileOrEmail(identifier);
    
    if (!validation.isValid) {
      setError(validation.error);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = validation.type === INPUT_TYPES.MOBILE
        ? { mobileNumber: identifier }
        : { email: identifier };
      
      const result = await authApi.sendForgotPasswordOtp(data);
      
      // Extract patient code from response - handle different response formats
      console.log('Forgot Password OTP Result:', result);
      // if (result?.data) {
      //   if (typeof result.data === 'string' && result.data.includes('patientcode=')) {
      //     const patientCodeMatch = result.data.split('patientcode=');
      //     if (patientCodeMatch[1]) {
      //       setPatientCode(patientCodeMatch[1]);
      //     }
      //   } else if (result.data.patientCode) {
      //     setPatientCode(result.data.patientCode);
      //   } else if (result.data.code) {
      //     setPatientCode(result.data.code);
      //   }
      // }
      
      setIdentifierType(validation.type);
      setOtpSent(true);
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [identifier, setLoading, setError, setIdentifierType, setOtpSent, setPatientCode]);
  
  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('loginObj') || '{}');
      
      if (userData?.sessionid && userData?.code) {
        await authApi.logout({
          sessionId: userData.sessionid,
          userCode: userData.code,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearUserSession();
      navigate('/');
    }
  }, [navigate]);
  
  /**
   * Resend OTP
   */
  const resendOtp = useCallback(async () => {
    setOtp('');
    return sendOtp();
  }, [sendOtp, setOtp]);
  
  /**
   * Verify forgot password OTP and get patientCode
   */
  const verifyForgotPasswordOtp = useCallback(async () => {
    const otpValidation = validateOtp(otp);
    
    if (!otpValidation.isValid) {
      setError(otpValidation.error);
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const data = identifierType === INPUT_TYPES.MOBILE
        ? { mobileNumber: identifier, otp }
        : { email: identifier, otp };
      
      const result = await authApi.verifyForgotPasswordOtp(data);
      
      // Extract patient code from response - handle different response formats
      console.log('Verify Forgot Password OTP Result:', result);
      if (result?.data) {
        if (typeof result?.data === 'string' && result.data.includes('patientcode=')) {
          const patientCodeMatch = result.data.split('patientcode=');
          if (patientCodeMatch[1]) {
            setPatientCode(patientCodeMatch[1]);
          }
        } else if (result?.data?.patientCode) {
          setPatientCode(result.data.patientCode);
        } else if (result?.data?.code) {
          setPatientCode(result.data.code);
        }
      }
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [otp, identifier, identifierType, setLoading, setError, setPatientCode]);
  
  /**
   * Reset password after OTP verification
   */
  const resetPassword = useCallback(async () => {
    const passwordValidation = validatePassword(password);
    
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return false;
    }
    
    if (password !== confirmPassword) {
      setError(ERROR_MESSAGES.PASSWORDS_MISMATCH);
      return false;
    }
    
    if (!patientCode) {
      setError('Session expired. Please try again.');
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Use Base64 encoding like the old code
      const data = {
        currentPasswordVal: Base64.encode(password),
        patientCode,
      };
      
      const result = await authApi.resetPassword(data);
      
      if (result?.status === 200 || result?.data) {
        return true;
      }
      
      return true;
    } catch (error) {
      setError(error.message || ERROR_MESSAGES.GENERIC_ERROR);
      return false;
    } finally {
      setLoading(false);
    }
  }, [password, confirmPassword, patientCode, setLoading, setError]);
  
  return {
    sendOtp,
    verifyOtp,
    loginWithPassword,
    checkPasswordAndProceed,
    checkEmailExists,
    register,
    setNewPassword,
    sendForgotPasswordOtp,
    verifyForgotPasswordOtp,
    resetPassword,
    logout,
    resendOtp,
  };
}
